<template>
  <div class="filters">
    <div class="field company-field">
      <span>Компания</span>
      <div class="company-search">
        <input
          v-model="companyQuery"
          class="input company-input"
          type="search"
          placeholder="Название или ИНН"
          autocomplete="off"
          @input="onCompanyInput"
          @focus="showSuggestions = true"
          @keydown.enter.prevent="applyAndHide"
        />
        <ul v-if="showSuggestions && companyQuery && filteredCompanies.length" class="suggestions">
          <li
            v-for="company in filteredCompanies"
            :key="company.id"
            @mousedown.prevent="selectCompany(company)"
          >
            <span class="company-name">{{ company.name }}</span>
            <span class="company-inn">{{ company.inn }}</span>
          </li>
        </ul>
      </div>
    </div>

    <label class="field">
      <span>Фамилия</span>
      <input
        v-model.trim="localFilters.driver"
        class="input"
        type="text"
        placeholder="Фамилия"
        @keydown.enter.prevent="applyAndHide"
      />
    </label>

    <label class="field">
      <span>Автомобиль</span>
      <input
        v-model.trim="localFilters.car"
        class="input"
        type="text"
        placeholder="Гос. номер"
        @keydown.enter.prevent="applyAndHide"
      />
    </label>

    <label class="field">
      <span>Прицеп</span>
      <input
        v-model.trim="localFilters.trailer"
        class="input"
        type="text"
        placeholder="Гос. номер"
        @keydown.enter.prevent="applyAndHide"
      />
    </label>

    <label class="field">
      <span>Доступность</span>
      <select v-model="localFilters.availability" class="select">
        <option value="">Все</option>
        <option value="available">Доступен</option>
        <option value="unavailable">Недоступен</option>
      </select>
    </label>

    <div class="filter-actions">
      <button class="btn filter-reset" type="button" :disabled="loading" @click="emitReset">
        Сбросить
      </button>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue';
import {
  buildExecutorSearchPayload,
  parseExecutorSearchPayload,
} from '@/pages/securityCheck/helpers/executorSearchPayload.mjs';

export default {
  name: 'ExecutorFilters',
  props: {
    companies: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    initialFilters: {
      type: Object,
      default: null,
    },
  },
  emits: ['apply', 'reset'],
  setup(props, { emit }) {
    const companyQuery = ref('');
    const selectedCompany = ref(null);
    const showSuggestions = ref(false);
    let applyTimer = null;

    const localFilters = reactive({
      driver: '',
      car: '',
      trailer: '',
      availability: '',
    });

    const hydrateFromPayload = (payload) => {
      if (!payload) return;

      const parsed = parseExecutorSearchPayload(payload);
      selectedCompany.value = parsed.companyId
        ? props.companies.find((company) => company.id === parsed.companyId) || { id: parsed.companyId }
        : null;
      companyQuery.value = selectedCompany.value?.name || parsed.companyQuery;
      localFilters.driver = parsed.driver;
      localFilters.car = parsed.car;
      localFilters.trailer = parsed.trailer;
      localFilters.availability = parsed.availability;
    };

    if (props.initialFilters) {
      hydrateFromPayload(props.initialFilters);
    }

    watch(
      () => props.initialFilters,
      (value) => hydrateFromPayload(value),
    );

    watch(
      () => props.companies,
      () => {
        if (props.initialFilters?.company?.id && !selectedCompany.value?.name) {
          hydrateFromPayload(props.initialFilters);
        }
      },
    );

    const filteredCompanies = computed(() => {
      const query = companyQuery.value.trim().toLowerCase();
      if (!query) return [];

      return props.companies.filter((company) => {
        const name = String(company.name || '').toLowerCase();
        const inn = String(company.inn || '');
        return name.includes(query) || inn.includes(query);
      }).slice(0, 12);
    });

    const onCompanyInput = () => {
      selectedCompany.value = null;
      showSuggestions.value = true;
      scheduleApply();
    };

    const selectCompany = (company) => {
      selectedCompany.value = company;
      companyQuery.value = company.name;
      showSuggestions.value = false;
      scheduleApply();
    };

    const buildPayload = () => buildExecutorSearchPayload({
      companyId: selectedCompany.value?.id || null,
      companyQuery: companyQuery.value,
      driver: localFilters.driver,
      car: localFilters.car,
      trailer: localFilters.trailer,
      availability: localFilters.availability,
    });

    const emitApply = () => {
      if (applyTimer) clearTimeout(applyTimer);
      emit('apply', buildPayload());
    };

    const applyAndHide = () => {
      showSuggestions.value = false;
      emitApply();
    };

    const scheduleApply = () => {
      if (applyTimer) clearTimeout(applyTimer);
      applyTimer = setTimeout(emitApply, 250);
    };

    const emitReset = () => {
      companyQuery.value = '';
      selectedCompany.value = null;
      showSuggestions.value = false;
      localFilters.driver = '';
      localFilters.car = '';
      localFilters.trailer = '';
      localFilters.availability = '';
      if (applyTimer) clearTimeout(applyTimer);
      emit('reset');
    };

    watch(companyQuery, (value) => {
      if (!value) {
        selectedCompany.value = null;
        showSuggestions.value = false;
      }
    });

    watch(
      () => [localFilters.driver, localFilters.car, localFilters.trailer, localFilters.availability],
      scheduleApply,
    );

    onBeforeUnmount(() => {
      if (applyTimer) clearTimeout(applyTimer);
    });

    return {
      companyQuery,
      localFilters,
      showSuggestions,
      filteredCompanies,
      onCompanyInput,
      selectCompany,
      emitApply,
      applyAndHide,
      emitReset,
    };
  },
};
</script>

<style scoped>
.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-end;
  margin-bottom: 12px;
  padding: 10px;
  background: #f7f8fa;
  border: 1px solid #edf0f3;
  border-radius: 6px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 3px;
  color: #4b5563;
  font-size: 12px;
}

.company-field {
  min-width: 280px;
}

.company-search {
  position: relative;
}

.company-input {
  min-width: 280px;
}

.input,
.select {
  height: 32px;
  border: 1px solid #d7dce2;
  border-radius: 4px;
  background: #fff;
  color: #333;
  font-size: 13px;
  padding: 0 8px;
}

.input {
  min-width: 150px;
}

.select {
  min-width: 150px;
}

.suggestions {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 20;
  width: 100%;
  max-height: 220px;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  overflow: auto;
  background: #fff;
  border: 1px solid #d7dce2;
  border-radius: 4px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
}

.suggestions li {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 13px;
}

.suggestions li:hover {
  background: #f3f6fb;
}

.company-name {
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.company-inn {
  color: #667085;
  flex-shrink: 0;
}

.filter-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.btn {
  height: 32px;
  border: 1px solid #d7dce2;
  border-radius: 4px;
  background: #fff;
  color: #333;
  cursor: pointer;
  font-size: 13px;
  padding: 0 12px;
}

.btn:hover {
  border-color: #007bff;
  color: #007bff;
}

.btn.primary {
  background: #007bff;
  border-color: #007bff;
  color: #fff;
}

.btn.primary:hover {
  background: #0069d9;
  border-color: #0069d9;
  color: #fff;
}

.btn:disabled {
  cursor: default;
  opacity: 0.6;
}

.filter-reset {
  margin-left: 0;
}

@media (max-width: 900px) {
  .filter-actions {
    margin-left: 0;
    width: 100%;
  }

  .company-field,
  .company-input {
    min-width: 100%;
  }
}
</style>
